import { useEffect, useMemo, useState, type FC } from 'react';
import {
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalHeader,
  RadioButton,
  RadioButtonGroup,
  Search,
  Tag,
} from '@carbon/react';
import { CheckmarkFilled, WarningAltFilled } from '@carbon/react/icons';
import { formatTcWorkspaceDate } from 'utils/dates';
import type { WellboreGeometryListRow } from 'types/well-workspace';
import { GeometrySheetHeader } from 'pages/pre-drill-wellbore-workspace/geometry-sheet-header';
import 'components/inline-edit/ibm-products-config';
import { useSelectGeometrySource, useWellboreGeometries } from '../hooks';
import styles from './wellbore-geometry-source-modal.module.scss';

export interface WellboreGeometrySourceModalProps {
  open: boolean;
  /** The Well Workspace the row is read under; both calls live at its bore address. */
  wellWorkspaceId: number;
  /** The bore whose source is being changed; null while no row has been picked. */
  uwi14: string | null;
  /** The well's name, for the bar under the title. */
  wellName: string | null;
  onClose: () => void;
  /** The pick was saved; the host re-reads its Wellbores so the row follows. */
  onSaved: () => void;
}

// An original row was authored by no workspace, so it has no name to show.
const rowName = (row: WellboreGeometryListRow): string =>
  row.name ?? 'Original';

// The radio value. The filed survey is no geometry row and carries no id.
const rowKey = (row: WellboreGeometryListRow): string => {
  return row.id === null ? 'filed-survey' : String(row.id);
};

// One state per row, the way the workspaces listing tags its rows: the asking
// workspace's own edit is a Draft, a promoted geometry is Promoted, and a row nobody
// authored is the bore's Original.
const rowStatus = (
  row: WellboreGeometryListRow
): { label: string; type: 'gray' | 'green' | 'cool-gray' } => {
  if (row.origin === 'original')
    return { label: 'Original', type: 'cool-gray' };
  if (row.promoted) return { label: 'Promoted', type: 'green' };
  return { label: 'Draft', type: 'gray' };
};

/**
 * Wellbore Geometry Source — the Well Workspace's picker for which geometry drives a
 * Wellbores row. Reads `POST /queries/wellbore_geometries` and saves through the bore's
 * `default_selections`; its bar is the geometry tearsheet's header without the arrow.
 * @param {WellboreGeometrySourceModalProps} props The bore, its workspace and the exits.
 * @return {JSX.Element} The modal.
 */
export const WellboreGeometrySourceModal: FC<
  WellboreGeometrySourceModalProps
> = ({ open, wellWorkspaceId, uwi14, wellName, onClose, onSaved }) => {
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const { data, isPending: loading } = useWellboreGeometries(
    wellWorkspaceId,
    uwi14,
    open
  );
  const {
    saving,
    error: submitError,
    select,
    reset,
  } = useSelectGeometrySource(wellWorkspaceId, uwi14, () => {
    onSaved();
    onClose();
  });

  // Every open starts clean: no search, no stale refusal.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    reset();
  }, [open, reset]);

  // Served in the order a radio group wants, so no sort here.
  const rows = useMemo<WellboreGeometryListRow[]>(
    () => data?.results ?? [],
    [data]
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => rowName(row).toLowerCase().includes(needle));
  }, [query, rows]);

  // The radio starts on this workspace's pick, which is held until it promotes and which
  // nothing else sees before then; with no pick, on the geometry driving the row today.
  const current =
    rows.find((row) => row.isDefault) ?? rows.find((row) => row.isCurrent);
  const currentKey = current ? rowKey(current) : null;
  useEffect(() => {
    if (open) setSelectedKey(currentKey);
  }, [open, currentKey]);

  const selected = rows.find((row) => rowKey(row) === selectedKey);
  // The filed survey is chosen by clearing the pick. The write names the standing pick, or any
  // of the bore's rows when nobody has picked, which the backend accepts as the same request.
  const clearTarget =
    rows.find((row) => row.isDefault && row.id !== null) ??
    rows.find((row) => row.id !== null);
  const canSave =
    selected !== undefined && selectedKey !== currentKey && !saving;

  const handleSave = () => {
    if (!selected) return;
    if (selected.selectionMethod === 'clear') {
      if (clearTarget !== undefined && clearTarget.id !== null) {
        select({ method: 'clear', geometryId: clearTarget.id });
      }
      return;
    }
    if (selected.id !== null) {
      select({ method: 'select', geometryId: selected.id });
    }
  };

  const rowLabel = (row: WellboreGeometryListRow) => {
    const status = rowStatus(row);
    return (
      <span className={styles.rowLabel}>
        <span className={styles.rowTitle}>
          <span className={styles.rowName}>{rowName(row)}</span>
          <Tag type={status.type} size="sm">
            {status.label}
          </Tag>
          {row.isCurrent && (
            <span className={styles.verdict}>
              <CheckmarkFilled className={styles.verdictCorrect} />
              Current
            </span>
          )}
          {row.supersededByNewer && (
            <span className={styles.verdict}>
              <WarningAltFilled className={styles.verdictSuperseded} />
              Superseded by a newer promotion
            </span>
          )}
          {row.sourceSurveyChanged && (
            <span className={styles.verdict}>
              <WarningAltFilled className={styles.verdictSuperseded} />
              Survey re-filed since this edit
            </span>
          )}
        </span>
        <span className={styles.rowMeta}>
          Created by {row.createdBy.displayName} · Created on{' '}
          {formatTcWorkspaceDate(row.createdAt)} · Updated{' '}
          {formatTcWorkspaceDate(row.updatedAt)}
        </span>
      </span>
    );
  };

  return (
    <ComposedModal
      open={open}
      onClose={onClose}
      size="lg"
      className={styles.modal}
    >
      <ModalHeader title="Wellbore Geometry Source" />
      <ModalBody aria-label="Wellbore Geometry Source" className={styles.body}>
        <GeometrySheetHeader
          wellName={wellName ?? ''}
          uwi14={uwi14 ?? ''}
          saving={saving}
          onCancel={onClose}
          onSave={handleSave}
          saveDisabled={!canSave}
        />
        <div className={styles.content}>
          {submitError && (
            <InlineNotification
              kind="error"
              title="Couldn’t change the wellbore geometry source"
              subtitle={submitError}
              lowContrast
              hideCloseButton
              className={styles.notification}
            />
          )}
          <p className={styles.description}>
            Select the workspace that should drive this row’s wellbore geometry,
            then Save. Workspaces appear here once they have changes ready to
            drive the geometry.{' '}
            {/* A pick made here is held until this workspace promotes, whichever row it
                names, the filed survey included. */}
            {selected?.selectionMethod === 'clear' ? (
              <>
                The row goes back to its filed survey{' '}
                <strong>when this workspace is promoted</strong>.
              </>
            ) : (
              <>
                Your selection is applied to the well record{' '}
                <strong>when this workspace is promoted</strong>.
              </>
            )}
          </p>
          <Search
            size="lg"
            labelText="Search workspaces"
            placeholder="Search workspaces..."
            value={query}
            onChange={(event) => setQuery(event.target.value ?? '')}
          />
          <div className={styles.list}>
            {loading ? (
              <InlineLoading description="Loading workspaces…" />
            ) : filtered.length === 0 ? (
              <div className={styles.empty}>No source found</div>
            ) : (
              <RadioButtonGroup
                name="wellbore-geometry-source"
                orientation="vertical"
                className={styles.radioGroup}
                valueSelected={selectedKey ?? ''}
                onChange={(value) => setSelectedKey(String(value))}
              >
                {filtered.map((row) => (
                  <RadioButton
                    key={rowKey(row)}
                    className={styles.row}
                    value={rowKey(row)}
                    labelText={rowLabel(row)}
                    // Reads as the mock has it, not as the visible name alone.
                    aria-label={`Use ${rowName(row)} as the geometry source`}
                  />
                ))}
              </RadioButtonGroup>
            )}
          </div>
          <p className={styles.rowCount}>
            {filtered.length} Row{filtered.length === 1 ? '' : 's'}
          </p>
        </div>
      </ModalBody>
    </ComposedModal>
  );
};
